/* eslint-disable @typescript-eslint/ban-types */
import QueryTags from '../../types/QueryTags'
import { 
  GetSecurityQuestionsResponse, 
  LoginResponse, 
  ResettingCredentialsResponse, 
  SetResetCredentialsResponse, 
  UserNameAvailabilityCheckResponse, 
  VerifyForgetCredentialsResponse, 
  VerifySecurityQuestionResponse,
  ForgetCredentialsResponse,
  FetchSecurityQuestionsResponse,
  MigrateduserResponse,
  VerifyExistingCustomerResponse,
  verifySelfonboardingOTPResponse,
  SaveCustomerDetailsResponse,
  SetSelfonboardingCredentialsResponse,
  VerifyCustomerUsernameResponse,
  SolveAccChallengeResponse,
  VerifySelfResetOTPResponse,
  SetNewPasswordResponse
} from './types/response'

import { 
  ForgetCredentialsRequest, 
  GetSecurityQuestionsRequest, 
  GetUsernamesRequest, 
  LoginRequest, 
  ResettingCredentialsRequest, 
  SelfActivate, 
  SetResetCredentialsRequest, 
  UserNameAvailabilityCheckRequest, 
  VerifyForgetCredentialsRequest, 
  VerifySecurityQuestionRequest,
  MigratedUserRequest,
  VerifyExistingCustomerRequest,
  verifySelfonboardingOTPRequest,
  SaveCustomerDetailsRequest,
  SetSelfonboardingCredentialsRequest,
  VerifyCustomerUsernameRequest,
  SolveAccChallengeRequest,
  VerifySelfResetOTPRequest,
  SetNewPasswordRequest
} from './types/request'


import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react'
import SelfActivateResponse from './types/response/selfActivate'
import SubmitSecurityQuestionsRequest from './types/request/securityQuestions/SubmitSecurityQuestionsRequest'


const authBasePath = '/auth/v1'
const administrationBasePath = '/administration/api'

const authEndpoints = (builder: EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>, QueryTags, 'data'>) =>
  ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (body) => ({
        url: `${authBasePath}/login`,
        method: 'POST',
        body,
      })
    }),
    logout: builder.mutation<string, string>({
      query: () => {
        return {
          url: `${authBasePath}/logout`
        }
      }
    }),
    keepAlive: builder.query<string, string>({
      query: () => `${authBasePath}/keep-alive`
    }),

    checkUserNameAvailability: builder.query<UserNameAvailabilityCheckResponse, UserNameAvailabilityCheckRequest>({
      query: ({ username }) => {
        return `${administrationBasePath}/customers?username=${username}`
      }
    }),

    resetCredentials: builder.mutation<
    ResettingCredentialsResponse,
    ResettingCredentialsRequest
  >({
    query: (body) => {
      const { customerId, resetCredentials } = body
      return {
        url: `${administrationBasePath}/migrated-customers/${customerId}/credentials`,
        method: 'PATCH',
        body: resetCredentials,
      }
    },
  }),

    selfActivate: builder.mutation<SelfActivateResponse, SelfActivate>({
      query: ({customerIdentityId}) => ({
        url: `${administrationBasePath}/customers/${customerIdentityId}/activate-channel`,
        method: 'PATCH',
      })
    }),

    fetchSecurityQuestions: builder.query<FetchSecurityQuestionsResponse, null>({
      query:() => `${administrationBasePath}/security-questions`
    }),

    submitSecurityQuestions: builder.mutation<string, SubmitSecurityQuestionsRequest>({
      query: ({questionsAndAnswers, profileId}) => ({
        url: `${administrationBasePath}/customers/${profileId}/security-questions-answers`,
        method: 'POST',
        body: {questionsAndAnswers: questionsAndAnswers}
      })
    }),
    changeUsername: builder.mutation<string, {username:string, profileId:string,}>({
      query: ({username, profileId}) => ({
        url: `${administrationBasePath}/users/${profileId}/change-username?username=${username}`,
        method: 'POST'
      })
    }),
    forgetCredentials: builder.mutation<ForgetCredentialsResponse, ForgetCredentialsRequest>({
      query: (body) => ({
        url: `${authBasePath}/forgetCredentials`,
        method: 'POST',
        body,
      })
    }),

    verifyForgetCredentials: builder.mutation<VerifyForgetCredentialsResponse, VerifyForgetCredentialsRequest>({
      query: ({ type, ...body }) => ({
        url: `${authBasePath}/forgetCredentials/verifyOtp`,
        method: 'POST',
        params: { type },
        body,
      })
    }),

    getUsernames: builder.query<string[], GetUsernamesRequest>({
      query: ({emailOrPhone}) => ({
        url: `${authBasePath}/forgetCredentials/${emailOrPhone}/usernames`,
        method: 'GET',
      })
    }),

    getSecurityQuestions: builder.query<GetSecurityQuestionsResponse[], GetSecurityQuestionsRequest>({
      query: (params) => ({
        url: `${authBasePath}/forgetCredentials/securityQuestion`,
        method: 'GET',
        params,
      })
    }),

    verifySecurityQuestion: builder.mutation<VerifySecurityQuestionResponse, VerifySecurityQuestionRequest>({
      query: ({profileId, ...body}) => ({
        url: `${authBasePath}/forgetCredentials/verifySecurityQuestion?profileId=${profileId}`,
        method: 'POST',
        body,
      })
    }),

    setResetCredentials: builder.mutation<SetResetCredentialsResponse, SetResetCredentialsRequest>({
      query: ({profileId, ...body}) => ({
        url: `${authBasePath}/forgetCredentials/setCredentials?profileId=${profileId}`,
        method: 'POST',
        body,
      })
    }),

    fetchMigratedUserCredentials: builder.mutation<MigrateduserResponse, MigratedUserRequest>({
      query: (body) => ({
        url: `${authBasePath}/migration/ib1/user-data`,
        method: 'POST',
        body,
      })
    }),

    verifyExistingCustomer: builder.mutation<VerifyExistingCustomerResponse, VerifyExistingCustomerRequest>({
      query: (body) => ({
        url: `${administrationBasePath}/applications/existing-customer-details`,
        method: 'POST',
        body,
      })
    }),
    verifySelfOnboardOtp: builder.mutation<verifySelfonboardingOTPResponse, verifySelfonboardingOTPRequest>({
      query: ({id, ...body}) => ({
        url: `${administrationBasePath}/applications/${id}/verify-otp`,
        method: 'POST',
        body
      })
    }),
    saveCustomerDetails: builder.mutation<SaveCustomerDetailsResponse, SaveCustomerDetailsRequest>({
      query: ({id, ...body}) => ({
        url: `${administrationBasePath}/applications/${id}/existing-customer`,
        method: 'PATCH',
        body,
      })
    }),
    setSafeAccess: builder.mutation<SetSelfonboardingCredentialsResponse, SetSelfonboardingCredentialsRequest>({
      query: ({id, ...body}) => ({
        url: `${administrationBasePath}/applications/${id}/credentials`,
        method: 'PATCH',
        body,
      })
    }),
    verifyCustomerUsername: builder.mutation<VerifyCustomerUsernameResponse, VerifyCustomerUsernameRequest>({
      query: ({...body}) => ({
        url: `${administrationBasePath}/users/generate-account-challenge`,
        method: 'POST',
        body,
      })
    }),
    solveAccountChallenge: builder.mutation<SolveAccChallengeResponse, SolveAccChallengeRequest>({
      query: ({...body}) => ({
        url: `${administrationBasePath}/users/solve-account-challenge`,
        method: 'POST',
        body,
      })
    }),
    verifySelfResetOtp: builder.mutation<VerifySelfResetOTPResponse, VerifySelfResetOTPRequest>({
      query: ({...body}) => ({
        url: `${administrationBasePath}/users/credentials/verify-otp`,
        method: 'POST',
        body,
      })
    }),
    addNewPassword: builder.mutation<SetNewPasswordResponse, SetNewPasswordRequest>({
      query: ({...body}) => ({
        url: `${administrationBasePath}/users/credentials/reset`,
        method: 'POST',
        body,
      })
    })
  })

export default authEndpoints
